/** Contract scope formatting — Initial Inspection attributes only. No cart/legacy/template fallbacks. */

import { parseMeasurementAttributeValue } from "./measurementAttribute";

export const SCOPE_EMPTY = "—";

export function getAttributeNumericValue(attr: any): number | null {
    if (!attr) return null;

    for (const raw of [attr.Qty, attr.CalculatedValue, attr.AttributeValue]) {
        if (raw === undefined || raw === null || raw === "") continue;
        const num = Number(raw);
        if (!isNaN(num)) return num;
    }
    return null;
}

export function getAttrRecords(attributes: any[], name: string): any[] {
    return attributes.filter((a: any) => a.AttributeName === name);
}

export function getAttrObj(attributes: any[], name: string): any | undefined {
    const records = getAttrRecords(attributes, name);
    if (records.length === 0) return undefined;

    for (let i = records.length - 1; i >= 0; i--) {
        const attr = records[i];
        const qty = getAttributeNumericValue(attr);
        if (qty !== null && qty > 0) return attr;
        const val = attr.AttributeValue;
        if (val !== null && val !== undefined && String(val).trim() !== "") {
            return attr;
        }
    }

    return records[records.length - 1];
}

export function getAttrValue(attributes: any[], name: string): string {
    const records = getAttrRecords(attributes, name);
    if (records.length === 0) return "";

    for (let i = records.length - 1; i >= 0; i--) {
        const val = records[i].AttributeValue;
        if (val !== null && val !== undefined && String(val).trim() !== "") {
            return String(val).trim();
        }
    }

    return "";
}

export function getScopeDisplay(attributes: any[], name: string): string {
    const val = getAttrValue(attributes, name);
    return val || SCOPE_EMPTY;
}

function getActualMeasurementValue(attributes: any[], name: string): number | null {
    const attr = getAttrObj(attributes, name);
    if (!attr) return null;

    const rawValue = attr.AttributeValue;
    if (typeof rawValue === "string" && rawValue.trim().startsWith("{")) {
        const entry = parseMeasurementAttributeValue(rawValue);
        const resolved =
            entry.Actual > 0 ? entry.Actual : entry.Calculated > 0 ? entry.Calculated : null;
        return resolved;
    }

    const num = Number(rawValue);
    return !Number.isNaN(num) && num > 0 ? num : null;
}

export function formatRoofAreaDisplay(attributes: any[]): string {
    const area = getActualMeasurementValue(attributes, "Area");
    return area !== null ? `${area} sq ft` : SCOPE_EMPTY;
}

export function formatUnderlaymentDisplay(attributes: any[]): string {
    return getScopeDisplay(attributes, "UnderlaymentType");
}

function formatActionLabel(action: string): string {
    switch (action) {
        case "Replace":
            return "R&R";
        case "Install":
            return "Inst";
        case "Detach & Reset":
            return "D&R";
        case "None":
            return "None";
        default:
            return action;
    }
}

export function formatIceWaterShieldDisplay(attributes: any[]): string {
    const qty = getActualMeasurementValue(attributes, "IceWaterBarrierSQ");
    if (qty === null) return SCOPE_EMPTY;

    const action = formatActionLabel(getAttrValue(attributes, "IceWaterBarrierAction"));
    return action ? `${qty} SF (${action})` : `${qty} SF`;
}

export function formatTearOffLayerDisplay(attributes: any[]): string {
    return getScopeDisplay(attributes, "TearOff");
}

export type PitchRow = {
    section: string;
    pitch: string;
    squares: string;
};

export function parsePitchRows(val: string): PitchRow[] {
    try {
        const parsed = JSON.parse(val);
        if (!Array.isArray(parsed)) return [];

        return parsed
            .map((entry: Record<string, unknown>) => {
                const pitch = entry.Pitch;
                const squares = entry.Squares;
                const section = entry.Name;

                if (
                    (pitch === undefined || pitch === null || pitch === "") &&
                    (squares === undefined || squares === null || squares === "")
                ) {
                    return null;
                }

                const pitchLabel =
                    pitch !== undefined &&
                    pitch !== null &&
                    String(pitch).trim() !== ""
                        ? String(pitch).trim()
                        : SCOPE_EMPTY;
                const sqNum = Number(squares);
                const squaresLabel =
                    squares !== undefined &&
                    squares !== null &&
                    String(squares).trim() !== "" &&
                    !Number.isNaN(sqNum) &&
                    sqNum > 0
                        ? String(sqNum)
                        : SCOPE_EMPTY;

                return {
                    section:
                        section !== undefined &&
                        section !== null &&
                        String(section).trim() !== ""
                            ? String(section).trim()
                            : SCOPE_EMPTY,
                    pitch: pitchLabel,
                    squares: squaresLabel,
                };
            })
            .filter((row): row is PitchRow => row !== null);
    } catch {
        return [];
    }
}

export function getPitchRows(attributes: any[]): PitchRow[] {
    const val = getAttrValue(attributes, "RoofPitch");
    if (!val) return [];
    return parsePitchRows(val);
}

export function formatPitchDisplay(attributes: any[]): string {
    const rows = getPitchRows(attributes);
    if (rows.length > 0) {
        return rows
            .map((row) => `${row.section} · ${row.pitch} · ${row.squares} SF`)
            .join("\n");
    }

    const val = getAttrValue(attributes, "RoofPitch");
    return val || SCOPE_EMPTY;
}

function formatQtyWithAction(
    attributes: any[],
    qtyName: string,
    unit: string,
): string {
    const qtyVal = getAttrValue(attributes, qtyName);
    const qtyNum = Number(qtyVal);
    if (!qtyVal || Number.isNaN(qtyNum) || qtyNum <= 0) return SCOPE_EMPTY;

    const actionName = qtyName.replace(/Qty$/, "Action");
    const action = formatActionLabel(getAttrValue(attributes, actionName));
    return action ? `${qtyNum} ${unit} (${action})` : `${qtyNum} ${unit}`;
}

function parseJsonArrayValue(raw: unknown): any[] {
    if (raw === null || raw === undefined || raw === "") return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
        const trimmed = raw.trim();
        if (!trimmed.startsWith("[")) return [];
        try {
            const parsed = JSON.parse(trimmed);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
}

function getJsonArrayAttribute(attributes: any[], name: string): any[] {
    const records = getAttrRecords(attributes, name);
    for (let i = records.length - 1; i >= 0; i--) {
        const parsed = parseJsonArrayValue(records[i].AttributeValue);
        if (parsed.length > 0) return parsed;
    }
    return [];
}

function entryField(entry: any, ...keys: string[]): string {
    for (const key of keys) {
        const val = entry?.[key];
        if (val !== undefined && val !== null && String(val).trim() !== "") {
            return String(val).trim();
        }
    }
    return "";
}

function entryCount(entry: any): number {
    return Number(entry?.Count ?? entry?.count ?? entry?.Qty ?? entry?.qty) || 0;
}

function formatFurnaceVents(attributes: any[]): string {
    const items = getJsonArrayAttribute(attributes, "FurnaceVents");
    if (items.length === 0) return SCOPE_EMPTY;

    const lines = items
        .filter((entry: any) => entryCount(entry) > 0)
        .map((entry: any) => {
            const count = entryCount(entry);
            const size = entryField(entry, "Size", "size") || SCOPE_EMPTY;
            return `${count}x ${size}`;
        });

    return lines.length > 0 ? lines.join("\n") : SCOPE_EMPTY;
}

function isInactiveChimneyEntry(entry: any): boolean {
    const count = entryCount(entry);
    const type = entryField(entry, "Type", "type").toLowerCase();
    const size = entryField(entry, "Size", "size").toLowerCase();
    return count <= 0 || type === "n/a" || size === "none";
}

function isInactiveSkylightEntry(entry: any): boolean {
    const count = entryCount(entry);
    const type = entryField(entry, "Type", "type").toLowerCase();
    const size = entryField(entry, "Size", "size").toLowerCase();
    return count <= 0 || type === "none" || size === "none";
}

function formatChimneyDetails(attributes: any[]): string {
    const items = getJsonArrayAttribute(attributes, "Chimneys");
    if (items.length === 0) return SCOPE_EMPTY;

    const lines = items
        .filter((entry) => !isInactiveChimneyEntry(entry))
        .map((entry) => {
            const count = entryCount(entry);
            const type = entryField(entry, "Type", "type") || SCOPE_EMPTY;
            const size = entryField(entry, "Size", "size") || SCOPE_EMPTY;
            const color = entryField(entry, "Color", "color");
            const colorSuffix = color ? `, ${color}` : "";
            return `${count}x ${type} — ${size}${colorSuffix}`;
        });

    return lines.length > 0 ? lines.join("\n") : SCOPE_EMPTY;
}

function formatSkylightDetails(attributes: any[]): string {
    const items = getJsonArrayAttribute(attributes, "Skylights");
    if (items.length === 0) return SCOPE_EMPTY;

    const lines = items
        .filter((entry) => !isInactiveSkylightEntry(entry))
        .map((entry) => {
            const count = entryCount(entry);
            const type = entryField(entry, "Type", "type") || SCOPE_EMPTY;
            const size = entryField(entry, "Size", "size") || SCOPE_EMPTY;
            const kit = entryField(entry, "FlashingKit", "flashingKit");
            const notes = entryField(entry, "Notes", "notes");
            const kitSuffix =
                kit && kit !== SCOPE_EMPTY && kit.toLowerCase() !== "n/a"
                    ? `, ${kit}`
                    : "";
            const notesSuffix = notes ? ` — ${notes}` : "";
            return `${count}x ${type} — ${size}${kitSuffix}${notesSuffix}`;
        });

    return lines.length > 0 ? lines.join("\n") : SCOPE_EMPTY;
}

function formatGutterActionFlag(
    action: string,
    column: "remove" | "install",
): string {
    if (!action) return SCOPE_EMPTY;

    switch (action) {
        case "Replace":
            return "Yes";
        case "Install":
            return column === "install" ? "Yes" : "No";
        case "Detach & Reset":
            return column === "install" ? "Yes" : "No";
        case "None":
            return "No";
        default:
            return SCOPE_EMPTY;
    }
}

function formatLengthDisplay(raw: string): string {
    const n = Number(raw);
    if (!raw || Number.isNaN(n) || n <= 0) return SCOPE_EMPTY;
    return `${n} LF`;
}

export function buildGutterScope(attributes: any[]) {
    const gutterLengthRaw = getAttrValue(attributes, "GutterLengthQty");
    const downspoutLengthRaw = getAttrValue(attributes, "DownspoutLengthQty");
    const gutterAction = getAttrValue(attributes, "GutterLengthAction");
    const downspoutAction = getAttrValue(attributes, "DownspoutLengthAction");

    return {
        RemoveExisting: formatGutterActionFlag(gutterAction, "remove"),
        InstallNew: formatGutterActionFlag(gutterAction, "install"),
        RemoveDownspouts: formatGutterActionFlag(downspoutAction, "remove"),
        InstallDownspouts: formatGutterActionFlag(downspoutAction, "install"),
        GutterLength: formatLengthDisplay(gutterLengthRaw),
        DownspoutLength: formatLengthDisplay(downspoutLengthRaw),
        GutterSize: getScopeDisplay(attributes, "GutterSize"),
        GutterColor: getScopeDisplay(attributes, "GutterColor"),
        DownspoutSize: getScopeDisplay(attributes, "DownspoutSize"),
        DownspoutColor: getScopeDisplay(attributes, "DownspoutColor"),
    };
}

/** Map cart nail products → contract nail size + qty fields. */
export function resolveNailSizesFromCart(cartItems: any[]): {
    FieldNailSize: string;
    FieldNailQty: string;
    RidgeNailSize: string;
    RidgeNailQty: string;
} {
    const items = Array.isArray(cartItems) ? cartItems : [];

    const pick = (category: string): { size: string; qty: string } => {
        const matches = items.filter((item) => {
            if (!item?.ItemCount || Number(item.ItemCount) <= 0) return false;
            const cat = String(item.ProductCategory || item.CategoryName || "").trim();
            return cat === category;
        });

        if (matches.length === 0) {
            return { size: SCOPE_EMPTY, qty: SCOPE_EMPTY };
        }

        // Prefer a supplier-selected line when present.
        const preferred =
            matches.find((item) => item.SupplierId) || matches[0];
        const name = String(
            preferred.ManufacturerProductName ||
                preferred.ProductName ||
                "",
        ).trim();
        const qtyNum = Number(preferred.ItemCount);
        const unit = String(preferred.UnitType || "box").trim() || "box";
        const qtyLabel =
            Number.isFinite(qtyNum) && qtyNum > 0
                ? `${qtyNum} ${unit}`
                : SCOPE_EMPTY;

        return {
            size: name || SCOPE_EMPTY,
            qty: qtyLabel,
        };
    };

    const field = pick("Roof Nails");
    const ridge = pick("H&R Nails");

    return {
        FieldNailSize: field.size,
        FieldNailQty: field.qty,
        RidgeNailSize: ridge.size,
        RidgeNailQty: ridge.qty,
    };
}

export function buildNailingVentilationScope(attributes: any[]) {
    const painted = getAttrValue(attributes, "VentilationPainted");
    const paintedSuffix =
        painted && painted.toLowerCase() === "yes" ? " — Painted YES" : "";

    const stepFlashing = formatQtyWithAction(
        attributes,
        "StepFlashingQty",
        "LF",
    );
    const plumbingJacks = formatQtyWithAction(
        attributes,
        "PlumbingJacksQty",
        "EA",
    );

    const ventilationType = getScopeDisplay(attributes, "VentilationType");

    return {
        StepFlashing:
            stepFlashing !== SCOPE_EMPTY
                ? `${stepFlashing}${paintedSuffix}`
                : SCOPE_EMPTY,
        RoofVentilation:
            ventilationType !== SCOPE_EMPTY
                ? `${ventilationType}${paintedSuffix}`
                : SCOPE_EMPTY,
        PlumbingVents:
            plumbingJacks !== SCOPE_EMPTY
                ? `${plumbingJacks}${paintedSuffix}`
                : SCOPE_EMPTY,
        FurnaceVent: formatFurnaceVents(attributes),
        ReflashChimney: formatChimneyDetails(attributes),
        ReflashSkylights: formatSkylightDetails(attributes),
        ReplaceSkylights: formatSkylightDetails(attributes),
    };
}
